import { Component, inject, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CollectionService } from "@bitwarden/common/abstractions/collection.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { OrganizationService } from "@bitwarden/common/abstractions/organization/organization.service.abstraction";
import { OrganizationApiServiceAbstraction } from "@bitwarden/common/abstractions/organization/organization-api.service.abstraction";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { PlanType } from "@bitwarden/common/enums/planType";
import { CollectionData } from "@bitwarden/common/models/data/collection.data";
import { Organization } from "@bitwarden/common/models/domain/organization";
import { CollectionRequest } from "@bitwarden/common/models/request/collection.request";
import { CollectionDetailsResponse } from "@bitwarden/common/models/response/collection.response";
import { OrganizationCreateRequest } from "@bitwarden/common/models/request/organization-create.request";
import { OrganizationKeysRequest } from "@bitwarden/common/models/request/organization-keys.request";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";

@Component({
  selector: "app-create-collection",
  templateUrl: "create-collection.component.html",
})
export class CreateCollectionComponent implements OnInit {
  private fb = inject(FormBuilder);
  private apiService = inject(ApiService);
  private collectionService = inject(CollectionService);
  private cryptoService = inject(CryptoService);
  private organizationService = inject(OrganizationService);
  private organizationApiService = inject(OrganizationApiServiceAbstraction);
  private stateService = inject(StateService);
  private platformUtilsService = inject(PlatformUtilsService);
  private i18nService = inject(I18nService);
  private logService = inject(LogService);

  // Org to create the collection in. Omit to create a new default org first.
  @Input() org?: Organization;
  @Input() onCreated?: (result: { orgId: string; collectionId?: string }) => void;

  form!: FormGroup;
  formPromise: Promise<unknown> | undefined;
  private isSubmitting = false;
  // Set once the server has created the team: no re-enable, so a retry can't make a duplicate.
  private created = false;

  submitButtonOptions = this.buildSubmitButton();

  ngOnInit() {
    this.form = this.fb.group({
      collectionName: [
        "",
        [Validators.required, Validators.pattern(/\S/), Validators.maxLength(50)],
      ],
    });
  }

  async createCollection() {
    if (this.isSubmitting || this.created) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.submitButtonOptions.enableButton(true);
      return;
    }

    this.isSubmitting = true;
    // Cleared per attempt so the toast check below reflects this submit, not a previous one.
    this.formPromise = undefined;
    try {
      const collectionName = (this.form.value.collectionName as string).trim();
      if (this.org) {
        await this.createInExistingOrg(this.org.id, collectionName);
      } else {
        await this.createWithDefaultOrg(collectionName);
      }
    } catch (e) {
      this.logService.error(String(e));
      // appApiAction surfaces API errors via validationService; only show a toast for
      // pre-API failures (crypto/state) where formPromise was never assigned.
      if (!this.formPromise) {
        this.showUnexpectedError();
      }
    } finally {
      this.isSubmitting = false;
      // A fresh button rather than enableButton(true): the submit-type button disabled itself on
      // click, and app-gloss-button is OnPush, so re-enabling the same object after an await never
      // re-renders and leaves "Create team" looking disabled after a failed submit.
      if (!this.created) {
        this.submitButtonOptions = this.buildSubmitButton();
      }
    }
  }

  private buildSubmitButton() {
    return new ActionButton({
      fullClass:
        "tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-bg-color-action-primary tw-px-6 tw-text-base tw-font-semibold tw-text-color-text-on-action tw-shadow-elevation-card",
      ghostShadow: true,
      text: this.i18nService.t("createTeam"),
      type: "submit",
      onClick: this.createCollection.bind(this),
    });
  }

  private async createInExistingOrg(orgId: string, collectionName: string) {
    const orgKey = await this.cryptoService.getOrgKey(orgId);
    if (!orgKey) {
      this.showUnexpectedError();
      return;
    }

    const encName = await this.cryptoService.encrypt(collectionName, orgKey);
    if (!encName.encryptedString) {
      this.logService.error("Failed to encrypt collection name");
      this.showUnexpectedError();
      return;
    }

    const request = new CollectionRequest();
    request.name = encName.encryptedString;

    const p = this.apiService.postCollection(orgId, request);
    this.formPromise = p;
    const collection = await p; // CollectionResponse
    // The team exists server-side from here on, so a failure below must not offer a retry.
    this.created = true;
    // postCollection doesn't trigger a sync, so the new collection is invisible to
    // getAllDecrypted() until we upsert it into local state ourselves.
    await this.collectionService.upsert(
      new CollectionData({ ...collection, readOnly: false } as CollectionDetailsResponse),
    );
    this.showCollectionCreatedSuccess();
    this.onCreated?.({ orgId, collectionId: collection.id });
  }

  private async createWithDefaultOrg(collectionName: string) {
    // A user's own teams all live in the one org they own; being a member of someone else's
    // team (their org) doesn't stop them having one. this.org is pinned at dialog-open time
    // (see openCreateTeamDialog), so a dialog opened before the org list finished loading
    // submits with org undefined even if the user's own org has since arrived. organizations$
    // is a stale BehaviorSubject snapshot until that load resolves; getAll() re-reads storage
    // at submit time, so an owned org found here gets the team instead of a second org.
    const ownedOrg = (await this.organizationService.getAll()).find((o) => o.isOwner);
    if (ownedOrg) {
      await this.createInExistingOrg(ownedOrg.id, collectionName);
      return;
    }

    const [billingEmail, [encOrgSymKey, orgSymKey]] = await Promise.all([
      this.stateService.getEmail(),
      this.cryptoService.makeShareKey(),
    ]);

    const [[orgPublicKey, encOrgPrivateKey], encCollectionNameResult] = await Promise.all([
      this.cryptoService.makeKeyPair(orgSymKey),
      this.cryptoService.encrypt(collectionName, orgSymKey),
    ]);

    const encKey = encOrgSymKey.encryptedString;
    const encPrivKey = encOrgPrivateKey.encryptedString;
    const encCollectionName = encCollectionNameResult.encryptedString;
    if (!encKey || !encPrivKey || !encCollectionName) {
      this.logService.error("Failed to generate organization keys");
      this.showUnexpectedError();
      return;
    }

    const request = new OrganizationCreateRequest();
    request.name = this.i18nService.t("myOrganization");
    request.billingEmail = billingEmail ?? "";
    request.planType = PlanType.Free;
    request.key = encKey;
    request.keys = new OrganizationKeysRequest(orgPublicKey, encPrivKey);
    // Server creates this as the org's default collection alongside the org itself.
    request.collectionName = encCollectionName;

    const p = this.organizationApiService.create(request);
    this.formPromise = p;
    const org = await p; // OrganizationResponse
    this.created = true;
    this.showCollectionCreatedSuccess();
    this.onCreated?.({ orgId: org.id });
  }

  private showUnexpectedError(): void {
    this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
  }

  private showCollectionCreatedSuccess(): void {
    this.platformUtilsService.showToast("success", "", this.i18nService.t("teamCreated"));
  }
}
